import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import agencyAdminService from '../../services/agencyAdminService';
import { 
  Users, UserCircle, Mail, Phone, Calendar, DollarSign,
  ChevronDown, ChevronRight, Search, RefreshCw, Loader2,
  AlertCircle, TrendingUp, Briefcase
} from 'lucide-react';
import '../../styles/modern-design-system.css';

const AdminClientsPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [clientsByAgent, setClientsByAgent] = useState({});
  const [expandedAgents, setExpandedAgents] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch all agents in the agency
      await fetchAgents();

      // Note: Fetching clients per agent would require backend support
      // For MVP, we'll structure the UI and note this limitation
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load clients data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await agencyAdminService.getAgents({
        status: 'all',
        role: 'all',
        sortBy: 'firstname',
        sortOrder: 'asc'
      });

      if (response.success && response.data && response.data.length > 0) {
        setAgents(response.data);
        
        // Fetch clients for each agent
        // TODO: This requires backend endpoint to fetch clients by agent ID
        // For now, we'll use a workaround with widget API if available
        await fetchClientsForAgents(response.data);
      } else {
        // Fallback: Try widget API
        try {
          const widgetResponse = await fetch(
            `${import.meta.env.VITE_API_URL}/api/widget/agents/${user?.id || user?.agency_id}`,
            { credentials: 'include' }
          );
          if (widgetResponse.ok) {
            const widgetData = await widgetResponse.json();
            if (widgetData.success && widgetData.data) {
              setAgents(widgetData.data);
              await fetchClientsForAgents(widgetData.data);
            }
          }
        } catch (widgetErr) {
          console.warn('Widget API fallback failed:', widgetErr);
        }
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load agents');
    }
  };

  const fetchClientsForAgents = async (agentsList) => {
    // This is a workaround - the standard /api/clients endpoint filters by authenticated user
    // For MVP, we'll need to either:
    // 1. Add a backend endpoint /api/admin/clients that returns all clients for agency
    // 2. Or modify the existing endpoint to support admin access
    // For now, we'll try to fetch using the widget approach or note the limitation
    
    const clientsMap = {};
    
    // Try to fetch clients - this may not work without backend support
    // For demonstration, we'll structure the data but may need backend changes
    for (const agent of agentsList) {
      try {
        // Note: This endpoint filters by authenticated user, so it won't work for other agents
        // This is a placeholder for the proper admin endpoint
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/clients`,
          { credentials: 'include' }
        );
        
        if (response.ok) {
          const clients = await response.json();
          // Only include clients if they match the current user (temporary limitation)
          if (user?.userid === agent.id || user?.id === agent.id) {
            clientsMap[agent.id] = Array.isArray(clients) ? clients : [];
          } else {
            clientsMap[agent.id] = [];
          }
        } else {
          clientsMap[agent.id] = [];
        }
      } catch (err) {
        console.warn(`Failed to fetch clients for agent ${agent.id}:`, err);
        clientsMap[agent.id] = [];
      }
    }
    
    setClientsByAgent(clientsMap);
  };

  const toggleAgentExpansion = (agentId) => {
    const newExpanded = new Set(expandedAgents);
    if (newExpanded.has(agentId)) {
      newExpanded.delete(agentId);
    } else {
      newExpanded.add(agentId);
    }
    setExpandedAgents(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '—';
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return '—';
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getClientInitials = (fullName) => {
    if (!fullName) return 'C';
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return fullName.substring(0, 2).toUpperCase();
  };

  const filteredAgents = agents.filter(agent => {
    const agentName = `${agent.name || agent.firstname || ''} ${agent.lastname || ''}`.toLowerCase();
    const agentEmail = (agent.email || '').toLowerCase();
    const matchesSearch = agentName.includes(searchQuery.toLowerCase()) || 
                         agentEmail.includes(searchQuery.toLowerCase());
    
    // Also check if any of their clients match the search
    const agentClients = clientsByAgent[agent.id] || [];
    const clientsMatch = agentClients.some(client => 
      (client.fullname || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.email || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    return matchesSearch || clientsMatch || searchQuery === '';
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients by Agent</h1>
          <p className="text-gray-600 mt-1">View all agents and their client portfolios</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => loadData()}
            className="ml-auto text-sm font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Note about backend limitation */}
      {agents.length > 0 && Object.keys(clientsByAgent).length === 0 && (
        <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Backend Support Required</p>
            <p className="text-sm mt-1">
              To view clients for all agents, a backend endpoint is needed that returns all clients for the agency, grouped by agent.
              Currently, the client list endpoint filters by the authenticated user's agent ID.
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search agents or clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Agents and Clients List */}
      {filteredAgents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No agents found</h3>
          <p className="text-gray-600">
            {searchQuery ? 'Try adjusting your search criteria' : 'No agents in your agency yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAgents.map((agent) => {
            const agentId = agent.id || agent.userid;
            const agentName = agent.name || `${agent.firstname || ''} ${agent.lastname || ''}`.trim();
            const isExpanded = expandedAgents.has(agentId);
            const clients = clientsByAgent[agentId] || [];
            const filteredClients = clients.filter(client => {
              if (!searchQuery) return true;
              const searchLower = searchQuery.toLowerCase();
              return (
                (client.fullname || '').toLowerCase().includes(searchLower) ||
                (client.email || '').toLowerCase().includes(searchLower) ||
                (client.phone || '').includes(searchQuery)
              );
            });

            return (
              <div key={agentId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Agent Header */}
                <button
                  onClick={() => toggleAgentExpansion(agentId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-700 rounded-lg">
                      <span className="text-sm font-bold text-white">
                        {agentName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{agentName}</h3>
                        {agent.role && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                            {agent.role}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        {agent.email && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Mail className="w-3 h-3" />
                            <span className="truncate">{agent.email}</span>
                          </div>
                        )}
                        {agent.phone && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Phone className="w-3 h-3" />
                            <span>{agent.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">{clients.length}</div>
                        <div className="text-xs text-gray-500">Clients</div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Agent Clients List */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    {filteredClients.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <UserCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">
                          {searchQuery ? 'No clients match your search' : 'No clients yet'}
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredClients.map((client) => (
                          <div key={client.clientid || client.id} className="p-4 hover:bg-white transition-colors">
                            <div className="flex items-start gap-4">
                              <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-700 rounded-lg flex-shrink-0">
                                <span className="text-sm font-bold text-white">
                                  {getClientInitials(client.fullname)}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                                      {client.fullname || 'Unknown Client'}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                                      {client.email && (
                                        <div className="flex items-center gap-1">
                                          <Mail className="w-3 h-3" />
                                          <span className="truncate">{client.email}</span>
                                        </div>
                                      )}
                                      {client.phone && (
                                        <div className="flex items-center gap-1">
                                          <Phone className="w-3 h-3" />
                                          <span>{client.phone}</span>
                                        </div>
                                      )}
                                      {client.relationship_status && (
                                        <div className="flex items-center gap-1">
                                          <Briefcase className="w-3 h-3" />
                                          <span className="capitalize">{client.relationship_status.replace('_', ' ')}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 text-right flex-shrink-0">
                                    {client.total_trips !== undefined && (
                                      <div>
                                        <div className="text-xs font-semibold text-gray-900">{client.total_trips || 0}</div>
                                        <div className="text-xs text-gray-500">Trips</div>
                                      </div>
                                    )}
                                    {client.lifetime_value !== undefined && client.lifetime_value > 0 && (
                                      <div className="relative group">
                                        <div>
                                          <div className="text-xs font-semibold text-green-600">
                                            {formatCurrency(client.lifetime_value)}
                                          </div>
                                          <div className="text-xs text-gray-500 cursor-help">LTV</div>
                                        </div>
                                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                          Lifetime Value - Total revenue from this client
                                          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    )}
                                    {client.createdat && formatDate(client.createdat) !== '—' && (
                                      <div className="hidden sm:block relative group">
                                        <div className="flex items-center gap-1 text-xs text-gray-500 cursor-help">
                                          <Calendar className="w-3 h-3" />
                                          <span>{formatDate(client.createdat)}</span>
                                        </div>
                                        <div className="absolute bottom-full left-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                          Client Since - Date client was first added
                                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminClientsPage;

